"use client"

import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { supabase } from "@/lib/supabase"
import type { PreviewData } from "@/lib/types"
import { compatible_previews } from "@/services/preview-service"
import { useAnalytics } from "@/services/analytics-service"
import LoadingState from "./loading-state"
import ErrorState from "./error-state"
import NotSharableState from "./not-sharable-state"
import SharedViewerContent from "./viewer-content"

export default function SharedViewerPage() {
  const { id } = useParams<{ id: string }>()
  const [preview, setPreview] = useState<PreviewData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [SubPreview, setSubPreview] = useState<PreviewData | null>(null)
  // Initialize analytics tracking (automatically tracks and manages session)
  useAnalytics(id || '')

  useEffect(() => {
    async function fetchPreviewDetails() {
      try {
        if (!id) {
          throw new Error("Preview ID is required")
        }

        setLoading(true)
        setError(null)

        const { data: previewObj, error } = await supabase
          .from("previews")
          .select(`*,model:available_models(name)`)
          .eq("id", id)
          .single()

        if (error) throw error
        if (!previewObj) throw new Error("Preview not found")

        setPreview(previewObj)
        if (previewObj.model_uuid) {
          const all_compatible_previews = await compatible_previews(previewObj.model_uuid)
          const selectedSubPreview = all_compatible_previews.find(p =>
            previewObj.sub_preview_id === p.id
          ) || null
          setSubPreview(selectedSubPreview)
        }

        // Analytics tracking is automatically handled by the useAnalytics hook
        // No need to manually start tracking here as it's already initialized
      } catch (err) {
        console.error("Error fetching preview:", err)
        setError(err instanceof Error ? err.message : "Failed to load preview")
      } finally {
        setLoading(false)
      }
    }

    fetchPreviewDetails()
  }, [id])

  return (
    <div className="min-h-screen flex flex-col">
      {loading ? (
        <LoadingState />
      ) : error || !preview ? (
        <ErrorState errorMessage={error || "Preview not found"} />
      ) : !preview.is_sharable ? (
        <NotSharableState />
      ) : (
        <SharedViewerContent preview={preview} SubPreview={SubPreview} />
      )}
    </div>
  )
}
